"use client";

import { useCallback, useMemo, useState } from "react";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import toast from "react-hot-toast";

import { useUserRole } from "@/hooks/useUserRole";
import { useSession } from "@/providers/SessionProvider";
import { propertiesActionsService } from "@/services/properties.actions";
import { canSeeArchivedProperties } from "@/types/property-status";

interface UseArchiveControlProps {
  propertyId: string;
  propertyTitle?: string;
  onArchiveSuccess?: () => void;
  onRestoreSuccess?: () => void;
  onError?: (error: Error) => void;
  userCanArchive?: boolean;
}

export function useArchiveControl({
  propertyId,
  propertyTitle,
  onArchiveSuccess,
  onRestoreSuccess,
  onError,
  userCanArchive=false,
}: UseArchiveControlProps) {
  const t = useTranslations();
  const queryClient = useQueryClient();
  const { isAuthenticated, user } = useSession();
  const { userRole, isAdmin, hasEnhancedAccess } = useUserRole();

  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [isRestoreModalOpen, setIsRestoreModalOpen] = useState(false);

  // Permissions
  const canArchive = useMemo(() => {
    return (hasEnhancedAccess || userCanArchive) && isAuthenticated;
  }, [hasEnhancedAccess, isAuthenticated, userCanArchive]);

  const canRestore = useMemo(() => {
    return canSeeArchivedProperties(userRole) && isAuthenticated;
  }, [userRole, isAuthenticated]);

  const canSeeArchived = useMemo(() => {
    return canSeeArchivedProperties(userRole);
  }, [userRole]);

  // Archive mutation
  const archiveMutation = useMutation({
    mutationFn: async (comment?: string) => {
      if (!canArchive) {
        throw new Error("Insufficient permissions to archive property");
      }

      return propertiesActionsService.archiveProperty(propertyId, comment);
    },
    onSuccess: (data) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ["properties"] });
      queryClient.invalidateQueries({ queryKey: ["property", propertyId] });
      queryClient.invalidateQueries({
        queryKey: ["realtor-property-details", propertyId],
      });
      queryClient.invalidateQueries({ queryKey: ["favorites"] });

      toast.success(t("realtor.actionsArchiveSuccess"));
      setIsArchiveModalOpen(false);
      onArchiveSuccess?.();
    },
    onError: (error) => {
      console.error("Failed to archive property:", error);
      if (error.statusCode === 403) {
        toast.error(t("error.hasNoAccess"));
      } else {
        toast.error(t("realtor.errorArchive"));
      }
      onError?.(error as Error);
    },
  });

  // Restore mutation
  const restoreMutation = useMutation({
    mutationFn: async (comment?: string) => {
      if (!canRestore) {
        throw new Error("Insufficient permissions to restore property");
      }

      // Use dedicated restore function
      return propertiesActionsService.restoreProperty(propertyId, comment);
    },
    onSuccess: (data) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ["properties"] });
      queryClient.invalidateQueries({ queryKey: ["property", propertyId] });
      queryClient.invalidateQueries({
        queryKey: ["realtor-property-details", propertyId],
      });

      toast.success(t("realtor.actionsRestoreSuccess"));
      setIsRestoreModalOpen(false);
      onRestoreSuccess?.();
    },
    onError: (error) => {
      console.error("Failed to restore property:", error);
      if (error.statusCode === 403) {
        toast.error(t("error.hasNoAccess"));
      } else {
        toast.error(t("realtor.errorRestore"));
      }
      onError?.(error as Error);
    },
  });

  // Modal handlers
  const openArchiveModal = useCallback(() => {
    if (!canArchive) {
      toast.error(t("realtor.insufficientPermissions"));
      return;
    }
    setIsArchiveModalOpen(true);
  }, [canArchive, t]);

  const openRestoreModal = useCallback(() => {
    if (!canRestore) {
      toast.error(t("realtor.insufficientPermissions"));
      return;
    }
    setIsRestoreModalOpen(true);
  }, [canRestore, t]);

  const closeArchiveModal = useCallback(() => {
    if (!archiveMutation.isPending) {
      setIsArchiveModalOpen(false);
    }
  }, [archiveMutation.isPending]);

  const closeRestoreModal = useCallback(() => {
    if (!restoreMutation.isPending) {
      setIsRestoreModalOpen(false);
    }
  }, [restoreMutation.isPending]);

  // Action handlers
  const handleArchive = useCallback(
    async (comment?: string) => {
      return archiveMutation.mutateAsync(comment);
    },
    [archiveMutation],
  );

  const handleRestore = useCallback(
    async (comment?: string) => {
      return restoreMutation.mutateAsync(comment);
    },
    [restoreMutation],
  );

  // Loading states
  const isArchiving = archiveMutation.isPending;
  const isRestoring = restoreMutation.isPending;
  const isLoading = isArchiving || isRestoring;

  // Error states
  const archiveError = archiveMutation.error;
  const restoreError = restoreMutation.error;
  const error = archiveError || restoreError;

  return {
    // Permissions
    canArchive,
    canRestore,
    canSeeArchived,

    // Modal states
    isArchiveModalOpen,
    isRestoreModalOpen,

    // Modal handlers
    openArchiveModal,
    openRestoreModal,
    closeArchiveModal,
    closeRestoreModal,

    // Actions
    handleArchive,
    handleRestore,

    // Loading states
    isArchiving,
    isRestoring,
    isLoading,

    // Error states
    archiveError,
    restoreError,
    error,

    // User context
    user,
    userRole,
    isAuthenticated,
    isAdmin,
    hasEnhancedAccess,

    // Reset functions
    resetArchive: archiveMutation.reset,
    resetRestore: restoreMutation.reset,
  };
}
